import React from 'react';
import { TableRow, TableCell, Stack, Avatar, Typography, useTheme } from '@mui/material';
import { PurchaseOrderItem, PurchaseOrderRecommendation } from '@domain/purchasing';
import { differenceInDays, format, startOfDay } from 'date-fns';
import { Shipment } from '@domain/shipment';
import { WarningAmber } from '@mui/icons-material';
import { Icon, IconSize } from '@shared/icons';
import JSEditableInput from '@shared/ui/common/JSEditableInput';
import { PurchaseOrderItemMoqMenu } from './PurchaseOrderItemMoqMenu';


interface PurchaseOrderItemRowProps {
    item: PurchaseOrderItem;
    shipment?: Shipment;
    onQuantityChange: (quantity: number | undefined, item: PurchaseOrderItem) => void;
    onOpenItemDetails: (item: PurchaseOrderItem) => void;
    recommendation?: PurchaseOrderRecommendation;
    /** When false, line total (qty × price) is hidden (draft uses toggle next to total). */
    showLineAmount?: boolean;
}

export const PurchaseOrderItemRow: React.FC<PurchaseOrderItemRowProps> = ({
    item,
    shipment,
    onQuantityChange,
    onOpenItemDetails,
    recommendation,
    showLineAmount = true,
}) => {
    const theme = useTheme();

    const formatPrice = (price: number, currency?: string) => {
        if (currency) {
            return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(price);
        }
        const priceString = price.toLocaleString();
        return priceString;
    }

    return (
        <TableRow
            onClick={() => onOpenItemDetails(item)}
            sx={{
                width: '100%',
                height: '88px',
                cursor: 'pointer',
            }}
        >

            <TableCell>
                <Stack direction="row" alignItems="center" gap={2}>
                    <Avatar
                        variant="rounded"
                        src={item.product.image}
                        sx={{ width: 40, height: 40, border: 1, borderColor: 'divider' }}
                    />

                    <Stack direction="column" gap={0}>
                        <Typography variant="body2" color="text.primary"
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                                display: '-webkit-box',
                                width: '100%',
                            }}
                        >
                            {item.product.title}
                        </Typography>

                        {item.productionReadyDate && shipment?.departureDate && item.productionReadyDate > shipment.departureDate ? (
                            <Typography variant="caption" color="error.main" display="flex" alignItems="center" gap={0.5}>
                                <WarningAmber sx={{ fontSize: 16 }} /> Production ready too late: {item.productionReadyDate ? format(item.productionReadyDate, 'dd MMM yyyy') : ''}
                            </Typography>
                        ) : (

                            <Stack direction="row" color="text.secondary" alignItems="center" gap={0.5}>
                                <Icon icon="CalendarCheck" size={IconSize.SMALL} />
                                <Typography variant="caption" noWrap>
                                    {item.shipment?.processedDate ? (
                                        `${format(item.shipment.processedDate, 'dd MMM yyyy')} (${differenceInDays(startOfDay(item.shipment.processedDate), startOfDay(new Date()))} days from now)`
                                    ) : item.eta ? `${format(item.eta, 'dd MMM yyyy')} (${differenceInDays(startOfDay(item.eta), startOfDay(new Date()))} days from now)` : ''}


                                </Typography>
                            </Stack>
                        )}

                    </Stack>
                </Stack>
            </TableCell>

            <TableCell>
                <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={2}>

                    {recommendation !== undefined && (
                        <PurchaseOrderItemMoqMenu item={item} recommendation={recommendation} />
                    )}

                    <JSEditableInput
                        value={item.quantity}
                        onValueChange={(value) => onQuantityChange(parseInt(value), item)}
                        outlined
                        suffix="items"
                        placeholder="0"
                        sx={{
                            backgroundColor: theme.palette.background.default,
                            minWidth: 100,
                        }}
                    />

                    {showLineAmount && item.purchasePrice && (
                        <Stack direction="row" alignItems="center" gap={0.5}>
                            <Typography variant="caption" color="text.secondary" noWrap>
                                {formatPrice((item.purchasePrice || 0) * (item.quantity || 0), item.currency)}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            </TableCell>
        </TableRow>
    );
};
